/**
 * client/src/lib/csp-nonce.ts — read the per-request CSP style nonce (#1499)
 *
 * server/static.ts stamps a fresh CSPRNG nonce into the served index.html's
 * `<meta name="csp-nonce">` tag on every production response, replacing the
 * `__CSP_NONCE__` placeholder baked in at build time (client/index.html).
 * Reading it via a DOM query (rather than a global `window` variable set by
 * an inline bootstrap <script>) needs no additional script-src allowance —
 * #1499 is scoped to style-src only, and this app mints no script nonce.
 *
 * Consumers (both wired at app bootstrap, before any component mounts):
 *  - main.tsx calls `setNonce()` (the `get-nonce` package used internally by
 *    react-remove-scroll / react-style-singleton) so Radix Dialog/Popover's
 *    scroll-lock <style> injection carries the nonce automatically.
 *  - App.tsx passes it to <ThemeProvider nonce={...}>, which forwards to
 *    next-themes — its disableTransitionOnChange <style> injector accepts a
 *    nonce prop directly. Currently DORMANT in this app
 *    (disableTransitionOnChange={false}) but wired for correctness.
 *
 * Returns undefined in dev (Vite serves client/index.html directly and never
 * runs it through server/static.ts, so the placeholder is never replaced —
 * harmless, since dev's CSP keeps 'unsafe-inline' for style-src-elem).
 */
export function getCspNonce(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const value = document
    .querySelector('meta[name="csp-nonce"]')
    ?.getAttribute("content");
  return value && value.length > 0 && value !== "__CSP_NONCE__" ? value : undefined;
}
